// Share Target Page
// Handles content shared to TrackFleet from other apps

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Share2, MapPin, Link as LinkIcon, FileText, X, Loader2 } from 'lucide-react';

interface SharedContent {
  title: string | null;
  text: string | null;
  url: string | null;
}

interface ParsedLocation {
  latitude: number;
  longitude: number;
  label?: string;
}

export default function SharePage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sharedContent, setSharedContent] = useState<SharedContent | null>(null);
  const [parsedLocation, setParsedLocation] = useState<ParsedLocation | null>(null);
  const [isProcessing] = useState(false);
  const [error] = useState<string | null>(null);

  // Parse location from various formats
  function parseLocation(
    title: string | null,
    text: string | null,
    url: string | null
  ): ParsedLocation | null {
    // Try to extract coordinates from text or URL
    const allText = [title, text, url].filter(Boolean).join(' ');

    // Pattern: latitude, longitude (e.g., "48.8566, 2.3522")
    const coordPattern = /(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/;
    const coordMatch = allText.match(coordPattern);

    if (coordMatch) {
      const lat = parseFloat(coordMatch[1]);
      const lng = parseFloat(coordMatch[2]);

      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return {
          latitude: lat,
          longitude: lng,
          label: title || undefined,
        };
      }
    }

    // Pattern: Google Maps URL
    const gmapsPattern = /@(-?\d+\.?\d*),(-?\d+\.?\d*)/;
    const gmapsMatch = allText.match(gmapsPattern);

    if (gmapsMatch) {
      return {
        latitude: parseFloat(gmapsMatch[1]),
        longitude: parseFloat(gmapsMatch[2]),
        label: title || undefined,
      };
    }

    // Pattern: geo: URI
    const geoPattern = /geo:(-?\d+\.?\d*),(-?\d+\.?\d*)/;
    const geoMatch = allText.match(geoPattern);

    if (geoMatch) {
      return {
        latitude: parseFloat(geoMatch[1]),
        longitude: parseFloat(geoMatch[2]),
        label: title || undefined,
      };
    }

    return null;
  }

  // Parse shared content from URL params
  useEffect(() => {
    const title = searchParams.get('title');
    const text = searchParams.get('text');
    const url = searchParams.get('url');

    if (title || text || url) {
      setSharedContent({ title, text, url });

      // Try to parse location from shared content
      const location = parseLocation(title, text, url);
      if (location) {
        setParsedLocation(location);
      }
    }
  }, [searchParams]);

  // Handle viewing location on map
  const handleViewOnMap = () => {
    if (parsedLocation) {
      const { latitude, longitude } = parsedLocation;
      navigate(`/map?lat=${latitude}&lng=${longitude}&zoom=15`);
    }
  };

  // Handle creating a geofence at this location
  const handleCreateGeofence = () => {
    if (parsedLocation) {
      const { latitude, longitude, label } = parsedLocation;
      navigate(
        `/geofences?action=create&lat=${latitude}&lng=${longitude}${
          label ? `&name=${encodeURIComponent(label)}` : ''
        }`
      );
    }
  };

  // Handle closing
  const handleClose = () => {
    navigate('/map');
  };

  // No shared content
  if (!sharedContent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
            <Share2 className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            {t('share_target.empty_title')}
          </h1>
          <p className="text-gray-500 mb-6">
            {t('share_target.empty_body')}
          </p>
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-cyan-600 text-white font-medium rounded-lg hover:bg-cyan-700 transition-colors"
          >
            {t('share_target.go_to_map')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white rounded-2xl shadow-lg max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Share2 className="w-6 h-6" />
              <h1 className="text-lg font-semibold">{t('share_target.header_title')}</h1>
            </div>
            <button
              onClick={handleClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Shared data preview */}
          <div className="space-y-3">
            {sharedContent.title && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">{t('share_target.field_title')}</p>
                  <p className="text-sm font-medium text-gray-900">
                    {sharedContent.title}
                  </p>
                </div>
              </div>
            )}

            {sharedContent.text && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">{t('share_target.field_text')}</p>
                  <p className="text-sm text-gray-700 break-all">
                    {sharedContent.text}
                  </p>
                </div>
              </div>
            )}

            {sharedContent.url && (
              <div className="flex items-start gap-3">
                <LinkIcon className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-500">{t('share_target.field_url')}</p>
                  <p className="text-sm text-cyan-600 break-all">
                    {sharedContent.url}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Parsed location */}
          {parsedLocation && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="w-5 h-5 text-green-600" />
                <span className="font-medium text-green-900">
                  {t('share_target.location_detected')}
                </span>
              </div>
              <p className="text-sm text-green-700">
                {parsedLocation.latitude.toFixed(6)}, {parsedLocation.longitude.toFixed(6)}
              </p>
              {parsedLocation.label && (
                <p className="text-xs text-green-600 mt-1">
                  {parsedLocation.label}
                </p>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 space-y-3">
          {parsedLocation ? (
            <>
              <button
                onClick={handleViewOnMap}
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-cyan-600 text-white font-medium rounded-xl hover:bg-cyan-700 transition-colors disabled:opacity-50"
              >
                {isProcessing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <MapPin className="w-5 h-5" />
                )}
                {t('share_target.view_on_map')}
              </button>

              <button
                onClick={handleCreateGeofence}
                disabled={isProcessing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-cyan-600 font-medium rounded-xl border-2 border-cyan-600 hover:bg-cyan-50 transition-colors disabled:opacity-50"
              >
                {t('share_target.create_geofence')}
              </button>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-gray-500 mb-4">
                {t('share_target.no_location_detected')}
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('share_target.close')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
